import {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import {diApi} from '@di-shared/api/di';
import {ExpectedError} from '@di-shared/features/common/CustomError';
import useApiError from '@di-shared/features/common/useApiError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import HeroTooltip from '@di-shared/features/public-service/vehicle-inspection/reservation/HeroTooltip';
import useVehicleInspectionReservationUtil from '@di-shared/features/public-service/vehicle-inspection/reservation/useVehicleInspectionReservationUtil';
import {ROUTE_PATHS} from '@di-shared/routes';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';
import {useSuspenseQuery} from '@tanstack/react-query';

export interface VehicleInspectionReservationDetailProps {
  puid: string;
  vin: string;
  reservationNumber: string;
}

function VehicleInspectionReservationDetail(props: VehicleInspectionReservationDetailProps): JSX.Element {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const {openAlert} = useCcWebviewUtilsHelper();
  return (
    <ErrorBoundary
      fallback={<Loading />}
      onError={async (error: any) => {
        await openAlert({
          title: t('T40169'),
          message: `error [${error.resultCode || 9999}-${props.reservationNumber}]`,
        });
        navigate(ROUTE_PATHS.HOME_FINANCE_TAB.getFullPath(props.vin), {replace: true});
      }}
    >
      <Suspense fallback={<Loading />}>
        <Inner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, puid, reservationNumber}: VehicleInspectionReservationDetailProps) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const {resolveError} = useApiError();
  const {openConfirm} = useCcWebviewUtilsHelper();
  const {inspectionLabels} = useVehicleInspectionReservationUtil();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {data: basicInfo} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.publicServiceInspection.getReservationStatus), puid, reservationNumber],
    queryFn: () => diApi.publicServiceInspection.getReservationStatus({puid, vin}),
    select(data) {
      if (!data.data || !data.data.statusList || data.data.statusList.length === 0) {
        throw new ExpectedError('Reservation not found');
      }
      const item = data.data.statusList.find((item) => item.reservationNumber === reservationNumber);
      if (!item) {
        throw new ExpectedError('Reservation not found');
      }
      return item;
    },
    refetchOnWindowFocus: true, // 수정 후 돌아올수도 있는 페이지라, 올떄마다 재로드
  });
  // queries 로 하지 않은 이유는 위 api 가 throw 되는 경우 이건 필요 없기 때문
  const {data: socialWeakData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.publicServiceInspection.getSocialWeakCodes)],
    queryFn: () => diApi.publicServiceInspection.getSocialWeakCodes(),
  });

  const [cancelReservation] = usePendingHandler(
    async () => {
      const isConfirm = await openConfirm({
        title: t('T10268'),
        message: t('T40217'),
        okText: t('T10247'),
        cancelText: t('T10221'),
      });
      if (!isConfirm) {
        return;
      }

      WebviewUtils.loading(true);
      await diApi.publicServiceInspection.cancelPayment({requestBody: {puid, reservationNumber}});
      WebviewUtils.loading(false);

      // 삭제 후, 공공서비스의 홈인 차량 유지 탭으로 replace 시킴 (본 페이지 재접근 안되도록)
      navigate(ROUTE_PATHS.HOME_FINANCE_TAB.getFullPath(vin), {replace: true});
    },
    async (error) => {
      WebviewUtils.loading(false);
      const [execute] = await resolveError(error);
      execute();
    }
  );

  const reservationDate = parseDateWithFormat(basicInfo.reservationDateTime);
  return (
    <>
      <main className="content">
        <section className="section">
          <div className="section_container_header">
            <h2 className="title_small_b">{t('T40189')}</h2>
          </div>
          <section className="reservation_info_wrap">
            <div className="reservation_info_box">
              <span className="title">{t('T40190')}</span>
              <p className="reservation_info_text">{inspectionLabels[basicInfo.inspectionCategory] || '-'}</p>
            </div>
            <div className="reservation_info_box">
              <span className="title">{t('T40191')}</span>
              <p className="reservation_info_text">{reservationDate ? CldrDatetimeFormatter.format(reservationDate, 'yMMMaHM') : '-'}</p>
            </div>
            <div className="reservation_info_box">
              <span className="title">{t('T40192')}</span>
              <p className="reservation_info_text">{basicInfo.licensePlateNumber}</p>
            </div>
            {basicInfo.isHero ? (
              // 의인인 경우
              <div className="reservation_info_box">
                <span className="title">
                  {t('T40194')}
                  <HeroTooltip />
                </span>
                <p className="reservation_info_text">{t('T40195')}</p>
              </div>
            ) : (
              // 의인 아닌 경우
              <div className="reservation_info_box">
                <span className="title">{t('T40197')}</span>
                <p className="reservation_info_text">
                  {socialWeakData.data.socialWeakCodes.find((item) => item.code === basicInfo.socialWeakCode)?.name || t('T40182')}
                </p>
              </div>
            )}
            <div className="reservation_info_box">
              <span className="title">{t('T40225')}</span>
              <p className="reservation_info_text">{t('T20384_$amount', {amount: formatNumberWithCommas(basicInfo.paymentAmount)})}</p>
            </div>
          </section>
        </section>
        <section className="section section_notice">
          <div className="section_container">
            <strong className="notice_title">{t('T40218')}</strong>
            <ul className="notice_list">
              <li
                className="notice_item"
                style={{whiteSpace: 'pre-wrap'}}
              >
                {t('T40219')}
              </li>
              <li className="notice_item">{t('T40220')}</li>
              <li className="notice_item">{t('T40221')}</li>
              <li className="notice_item">{t('T40222')}</li>
              <li className="notice_item">{t('T40223')}</li>
              <li className="notice_item">{t('T40224')}</li>
            </ul>
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={() => {
            WebviewUtils.openNewWebView({
              url:
                window.location.origin +
                ROUTE_PATHS.PUBLIC_SERVICE_VEHICLE_INSPECTION_RESERVATION_$RESERVATION_NUMBER_EDIT.getFullPath(vin, reservationNumber),
              isModal: true,
              isNavigationBar: false,
              isToolBar: false,
            });
          }}
        >
          {t('T10266')}
        </button>
        <button
          type="button"
          className="btn btn_large btn_outline btn_full"
          onClick={() => cancelReservation()}
        >
          {t('T10267')}
        </button>
      </div>
    </>
  );
}

function Loading() {
  const {t} = useInferableTranslation();
  return (
    <main className="content">
      <section className="section">
        <div className="section_container_header type_main">
          <h2 className="section_heading_title">{t('T40189')}</h2>
        </div>
        <section className="reservation_info_wrap">
          <div className="reservation_info_box skeleton">
            <span className="title">&nbsp;</span>
            <p className="reservation_info_text">&nbsp;</p>
          </div>
          <div className="reservation_info_box skeleton">
            <span className="title">&nbsp;</span>
            <p className="reservation_info_text">&nbsp;</p>
          </div>
          <div className="reservation_info_box skeleton">
            <span className="title">&nbsp;</span>
            <p className="reservation_info_text">&nbsp;</p>
          </div>
          <div className="reservation_info_box skeleton">
            <span className="title">&nbsp;</span>
            <p className="reservation_info_text">&nbsp;</p>
          </div>
          <div className="reservation_info_box skeleton">
            <span className="title">&nbsp;</span>
            <p className="reservation_info_text">&nbsp;</p>
          </div>
        </section>
      </section>
      <section className="section section_notice">
        <div className="section_container">
          <strong className="notice_title">{t('T40218')}</strong>
          <ul className="notice_list">
            <li
              className="notice_item"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T40219')}
            </li>
            <li className="notice_item">{t('T40220')}</li>
            <li className="notice_item">{t('T40221')}</li>
            <li className="notice_item">{t('T40222')}</li>
            <li className="notice_item">{t('T40223')}</li>
            <li className="notice_item">{t('T40224')}</li>
          </ul>
        </div>
      </section>
    </main>
  );
}

export default VehicleInspectionReservationDetail;
